import React, { RefObject, useCallback, useMemo } from 'react'
import { Platform, StyleSheet, TouchableOpacity, View, Image } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import JoinPremiumImage from '../../assets/joinPremium.png'
import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { ReduceMotion } from 'react-native-reanimated'

type JoinPremiumProps = {
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
  okayPress: (subscriber: boolean) => void
  ignorePress: (subscriber: boolean) => void
}

export const JoinPremiumSheet = ({ bottomSheetModalRef, ignorePress, okayPress }: JoinPremiumProps) => {
  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  )

  const percentage = Platform.OS === 'ios' ? '60%' : '67%'

  const snapPoints = useMemo(() => [percentage], [percentage])

  return (
    <DS.Frame>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        snapPoints={snapPoints}
        backdropComponent={renderBackdrop}
        detached={false}
        handleStyle={styles.bottomSheetHandleStyle}
        handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
        keyboardBlurBehavior="restore"
        overrideReduceMotion={ReduceMotion.System}
      >
        <BottomSheetView style={{ flex: 1 }}>
          <DS.Frame style={styles.imageContainer}>
            <Image source={JoinPremiumImage} style={styles.image} resizeMode="contain" />
          </DS.Frame>
          <DS.Frame style={styles.container}>
            <DS.Frame>
              <DS.Text variant="Heading 2/Medium" textAlign="left" style={{ marginTop: 8 }}>
                {translate('joinPremium.title')}
              </DS.Text>
              <DS.Text variant="Body Large/Regular" textAlign="left" style={{ marginTop: 8 }}>
                {translate('joinPremium.subtitle')}
              </DS.Text>
            </DS.Frame>

            <DS.Frame style={styles.actionsContainer}>
              <TouchableOpacity style={styles.okayButton} onPress={() => okayPress(true)}>
                <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                  {translate('joinPremium.getPremium')}
                </DS.Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ignoreButton} onPress={() => ignorePress(false)}>
                <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                  {translate('joinPremium.cancel')}
                </DS.Text>
              </TouchableOpacity>
            </DS.Frame>
          </DS.Frame>
        </BottomSheetView>
      </BottomSheetModal>
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 32
  },
  imageContainer: {
    paddingHorizontal: 12,
    backgroundColor: '#F6F2E9',
    alignItems: 'center'
  },
  image: {
    marginTop: -12,
    width: '100%'
  },
  bottomSheetHandleStyle: {
    backgroundColor: '#F6F2E9',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 0
  },
  bottomSheetIndicatorStyle: {
    display: 'none'
  },
  okayButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: DS.spacing[24],
    width: '100%',
    height: 48
  },
  ignoreButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    borderRadius: DS.spacing[24],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '100%',
    height: 48
  },
  actionsContainer: {
    flex: 1,
    paddingTop: 16,
    gap: 6,
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  }
})
